import React, { useState, useEffect } from 'react'
import BreadCrumbComp from '../../../components/breadCrumb'
import SimpleTable from '../../../components/tables/simpleTable'
import SimpleSelect from '../../../components/selects/simpleSelect'
import FormSelect from '../../../components/selects/formSelect'
import LaddaButton from '../../../components/buttons/laddaButton'
import CustomSnackbar from '../../../components/snackbar'
import { Grid } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { fetchTableData, fetchClientOptions } from './externalClientSatisfactionService'
import { externalClientSatisfactionColumns } from './columns'
import { icons } from '../../../constants'
import { callApiAsync, getExternalSurveyOrdersWithUrls } from '../../../services/apiService'
import { isRoleUtil } from '../../../utils/AuthorizedUtils/360Report'
import { useSelector } from 'react-redux'
import GenerateSurveyLinkModal from './generateSurveyLinkModal'
import ViewSurveyResultsModal from './viewSurveyResultsModal'

const ExternalClientSatisfaction = () => {
    const navigate = useNavigate()
    const [tableData, setTableData] = useState([])
    const [loading, setLoading] = useState(false)
    const [currentPage, setCurrentPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [totalRecords, setTotalRecords] = useState(0)
    const [clientOptions, setClientOptions] = useState([])
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')

    const today = new Date()
    const currentYear = today.getFullYear()

    const userData = useSelector((state) => state.userData.userData);
    const isExternalClientAdmin = isRoleUtil("EXTERNAL_CLIENT_ADMIN", userData.roleList);
    const [openGenerateLinkModal, setOpenGenerateLinkModal] = useState(false)
    const [openViewResultsModal, setOpenViewResultsModal] = useState(false)
    const [selectedAnswerId, setSelectedAnswerId] = useState(null)
    const [ordersWithUrls, setOrdersWithUrls] = useState([])
    const [selectedOrderUrl, setSelectedOrderUrl] = useState(null)

    const yearOptions = [
        { name: "Seleccionar Año", value: null },
        ...Array.from({ length: 5 }, (_, i) => ({
            name: String(currentYear - i),
            value: currentYear - i
        }))
    ]

    const [appliedFilters, setAppliedFilters] = useState({
        clientName: null,
        year: currentYear
    })

    const {
        control,
        getValues,
        reset,
        watch
    } = useForm({
        defaultValues: {
            client: null,
            year: currentYear,
            bannerOrderSelect: ''
        }
    })

    const watchBannerOrder = watch('bannerOrderSelect')

    const getTableData = async (page = 1, pageSize = 10) => {
        await fetchTableData(
            appliedFilters.clientName,
            appliedFilters.year,
            page,
            pageSize,
            setTableData,
            setTotalRecords,
            setLoading
        )
    }

    const handleFilter = () => {
        const { client, year } = getValues()
        const clientVal = client != null && typeof client === 'object' ? client.value : client
        const newFilters = {
            clientName: clientVal ?? null,
            year: year ?? currentYear
        }
        setAppliedFilters(newFilters)
        setCurrentPage(1)
        fetchTableData(
            newFilters.clientName,
            newFilters.year,
            1,
            rowsPerPage,
            setTableData,
            setTotalRecords,
            setLoading
        )
    }

    const handleClearFilters = () => {
        reset({
            client: null,
            year: currentYear
        })
        const cleared = {
            clientName: null,
            year: currentYear
        }
        setAppliedFilters(cleared)
        setCurrentPage(1)
        fetchTableData(
            cleared.clientName,
            cleared.year,
            1,
            rowsPerPage,
            setTableData,
            setTotalRecords,
            setLoading
        )
    }

    const handlePageChange = (page, rows) => {
        const nextPageSize = rows ?? rowsPerPage
        setCurrentPage(page)
        setRowsPerPage(nextPageSize)
        fetchTableData(
            appliedFilters.clientName,
            appliedFilters.year,
            page,
            nextPageSize,
            setTableData,
            setTotalRecords,
            setLoading
        )
    }

    const handleCopyLink = (surveyOrderId) => {
        if (surveyOrderId) {
            const surveyUrl = `${window.location.origin}/foreign_trade_board/external_client_satisfaction/international/answer/${surveyOrderId}`
            navigator.clipboard.writeText(surveyUrl).then(() => {
                setSnackbarMessage('Se copió el link de encuesta')
                setSnackbarOpen(true)
            }).catch(() => {
                setSnackbarMessage('Error al copiar el link')
                setSnackbarOpen(true)
            })
        }
    }

    const handleViewResults = (answerId) => {
        if (answerId) {
            setSelectedAnswerId(answerId)
            setOpenViewResultsModal(true)
        }
    }

    const handleCloseViewResultsModal = () => {
        setOpenViewResultsModal(false)
        setSelectedAnswerId(null)
    }

    const handleEditSurvey = () => {
        navigate('/foreign_trade_board/external_client_satisfaction/create')
    }

    const handleGenerateLink = () => {
        setOpenGenerateLinkModal(true)
    }

    const refreshOrdersWithUrls = () => {
        if (isExternalClientAdmin) {
            callApiAsync(
                () => getExternalSurveyOrdersWithUrls(),
                (resp) => {
                    const data = resp?.data ?? resp
                    setOrdersWithUrls(Array.isArray(data) ? data : [])
                },
                null
            )
        }
    }

    const handleCloseGenerateLinkModal = () => {
        setOpenGenerateLinkModal(false)
        refreshOrdersWithUrls()
    }

    const handleCopyBannerUrl = () => {
        if (!selectedOrderUrl) return
        const fullUrl = selectedOrderUrl.surveyUrl?.startsWith('http')
            ? selectedOrderUrl.surveyUrl
            : `${window.location.origin}${selectedOrderUrl.surveyUrl}`
        navigator.clipboard.writeText(fullUrl).then(() => {
            setSnackbarMessage('Se copió el link de encuesta')
            setSnackbarOpen(true)
        }).catch(() => {
            setSnackbarMessage('Error al copiar el link')
            setSnackbarOpen(true)
        })
    }

    useEffect(() => {
        getTableData(currentPage, rowsPerPage)
        fetchClientOptions(setClientOptions)
    }, [])

    useEffect(() => {
        if (isExternalClientAdmin) {
            callApiAsync(
                () => getExternalSurveyOrdersWithUrls(),
                (resp) => {
                    const data = resp?.data ?? resp
                    setOrdersWithUrls(Array.isArray(data) ? data : [])
                },
                null
            )
        }
    }, [isExternalClientAdmin])

    useEffect(() => {
        if (watchBannerOrder === '' || watchBannerOrder == null) {
            setSelectedOrderUrl(null)
            return
        }
        const i = parseInt(String(watchBannerOrder), 10)
        if (!Number.isNaN(i) && ordersWithUrls[i]) {
            setSelectedOrderUrl(ordersWithUrls[i])
        } else {
            setSelectedOrderUrl(null)
        }
    }, [watchBannerOrder, ordersWithUrls])

    const columnsWithActions = externalClientSatisfactionColumns.map(col => {
        if (col.field === 'actions') {
            return {
                ...col,
                render: (rowData) => col.render(rowData, handleCopyLink, handleViewResults)
            }
        }
        return col
    })

    return (
        <div>
            <BreadCrumbComp />
            <div className="searchTitle">
                <h1>SATISFACCIÓN CLIENTE INTERNACIONAL</h1>
            </div>

            <div className="table-container">
                <div className="HContainer j-space-between a-center">
                    <h1 className="title-bio-details-chard">
                        SATISFACCIÓN DE CLIENTE INTERNACIONAL
                    </h1>

                    {isExternalClientAdmin && (
                        <div>
                            <LaddaButton
                                variant="contained"
                                onClick={handleEditSurvey}
                                color={"#404040"}
                                backgroundColor={"#F4D658"}
                            >
                                <div className="IHContainer align-center">
                                    <p className="button_text">Crear Encuesta Satisfacción Cliente Internacional</p>
                                </div>
                            </LaddaButton>

                            <LaddaButton
                                variant="contained"
                                onClick={handleGenerateLink}
                                backgroundColor="#6C5CE7"
                            >
                                <div className="IHContainer align-center">
                                    <img
                                        className="btn-icon"
                                        src={icons.plusIcon}
                                        alt="icon"
                                        style={{ marginRight: "10px" }}
                                    />
                                    <p className="button_text">Generar enlace para orden de importación</p>
                                </div>
                            </LaddaButton>
                        </div>
                    )}
                </div>

                {isExternalClientAdmin && ordersWithUrls.length > 0 && (
                    <div
                        className="mb-10"
                        style={{
                            padding: '12px 16px',
                            backgroundColor: '#f0f0f5',
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0'
                        }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={6}>
                                <p className="info-label">URL de encuesta</p>
                                <div className="HContainer a-center" style={{ gap: '8px', flexWrap: 'wrap' }}>
                                    <span
                                        style={{
                                            fontSize: '14px',
                                            color: '#333',
                                            wordBreak: 'break-all',
                                            flex: '1 1 200px'
                                        }}
                                    >
                                        {selectedOrderUrl
                                            ? (selectedOrderUrl.surveyUrl?.startsWith('http')
                                                ? selectedOrderUrl.surveyUrl
                                                : `${window.location.origin}${selectedOrderUrl.surveyUrl}`)
                                            : 'Seleccione un Nº de orden'}
                                    </span>
                                    {selectedOrderUrl && (
                                        <span
                                            onClick={handleCopyBannerUrl}
                                            style={{
                                                color: '#6C5CE7',
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            Copiar link
                                        </span>
                                    )}
                                </div>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <p className="info-label">Nº de orden creados</p>
                                <SimpleSelect
                                    name="bannerOrderSelect"
                                    control={control}
                                    options={[
                                        { name: 'Seleccionar Nº de orden', value: '' },
                                        ...ordersWithUrls.map((o, i) => ({
                                            name: o.orderNumber || `Orden ${i + 1}`,
                                            value: String(i)
                                        }))
                                    ]}
                                    margin="0"
                                />
                            </Grid>
                        </Grid>
                    </div>
                )}

                <div className="mb-10">
                    <form>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={12} md={3}>
                                <p className="info-label">Seleccionar Cliente</p>
                                <FormSelect
                                    name="client"
                                    control={control}
                                    options={clientOptions}
                                    margin="0"
                                    formLabel="Seleccionar Cliente"
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <p className="info-label">Seleccionar Año</p>
                                <SimpleSelect
                                    name="year"
                                    control={control}
                                    options={yearOptions}
                                    margin={"0"}
                                />
                            </Grid>

                            <Grid item xs={12} md={3}>
                                <div className="IHContainer">
                                    <LaddaButton
                                        variant="contained"
                                        onClick={handleFilter}
                                        margin="0"
                                    >
                                        <img
                                            className="btn-icon"
                                            src={icons.filterWhite}
                                            alt="Filtrar"
                                        />
                                    </LaddaButton>
                                    <LaddaButton
                                        variant="contained"
                                        onClick={handleClearFilters}
                                        margin="0 0 0 10px"
                                    >
                                        <img
                                            className="btn-icon"
                                            src={icons.clearFilterWhite}
                                            alt="Limpiar filtros"
                                        />
                                    </LaddaButton>
                                </div>
                            </Grid>
                        </Grid>
                    </form>
                </div>

                <SimpleTable
                    data={tableData}
                    columns={columnsWithActions}
                    isPaginated={true}
                    serverPagination={true}
                    totalRecords={totalRecords}
                    rowsPerPage={rowsPerPage}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    scrollHeight="750px"
                    emptyMessage="No se han encontrado registros"
                />
            </div>

            <CustomSnackbar
                open={snackbarOpen}
                message={snackbarMessage}
                severity="success"
                onClose={() => setSnackbarOpen(false)}
            />

            <GenerateSurveyLinkModal
                open={openGenerateLinkModal}
                handleClose={handleCloseGenerateLinkModal}
            />

            <ViewSurveyResultsModal
                open={openViewResultsModal}
                handleClose={handleCloseViewResultsModal}
                answerId={selectedAnswerId}
            />
        </div>
    )
}

export default ExternalClientSatisfaction
